import { Suspense, lazy, useEffect, useState, useCallback } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { apiFetch, ensureCsrf } from "./api";
import { trackEvent } from "./analytics";
import { SEO_SITE_NAME, SEO_SITE_URL, usePageSeo } from "./seo";

import PageLoader from "./components/PageLoader";

const LandingPage = lazy(() => import("./pages/LandingPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const CashFlowPage = lazy(() => import("./pages/CashFlowPage"));
const SnapshotHistoryPage = lazy(() => import("./pages/SnapshotHistoryPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const Terms = lazy(() => import("./pages/Terms"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const CookiesPage = lazy(() => import("./pages/CookiesPage"));
const NetWorthTrackerPage = lazy(() => import("./pages/NetWorthTrackerPage"));
const CashTrackerAppPage = lazy(() => import("./pages/CashTrackerAppPage"));
const MintAlternativePage = lazy(() => import("./pages/MintAlternativePage"));
const HowToTrackNetWorthPage = lazy(() => import("./pages/HowToTrackNetWorthPage"));
const ConfirmEmailChangePage = lazy(() => import("./pages/ConfirmEmailChangePage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const CurrencyRequiredModal = lazy(() => import("./components/CurrencyRequiredModal"));
const Analytics = lazy(() =>
  import("@vercel/analytics/react").then((module) => ({ default: module.Analytics }))
);

const HOME_DESCRIPTION =
  "Track your total cash over time with quick snapshots, account breakdowns, and progress insights. Optional email reminders.";
const HOW_TO_TRACK_NET_WORTH_DESCRIPTION =
  "Learn how to track net worth with a simple monthly process, clear asset and debt categories, and consistent snapshot reviews, including debts as negative balances.";

const JSON_LD_ORGANIZATION = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SEO_SITE_NAME,
  url: SEO_SITE_URL,
};

const JSON_LD_WEB_APPLICATION = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: SEO_SITE_NAME,
  applicationCategory: "FinanceApplication",
  operatingSystem: "Web",
  url: `${SEO_SITE_URL}/`,
  description: HOME_DESCRIPTION,
};

const JSON_LD_HOW_TO_TRACK_NET_WORTH_ARTICLE = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "How to track net worth without linking bank accounts",
  description: HOW_TO_TRACK_NET_WORTH_DESCRIPTION,
  author: {
    "@type": "Organization",
    name: SEO_SITE_NAME,
  },
  publisher: {
    "@type": "Organization",
    name: SEO_SITE_NAME,
  },
  datePublished: "2026-02-10",
  dateModified: "2026-02-13",
  mainEntityOfPage: `${SEO_SITE_URL}/how-to-track-net-worth`,
};

const JSON_LD_HOW_TO_TRACK_NET_WORTH_FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How often should I update net worth?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Monthly is enough for most people and reduces emotional noise.",
      },
    },
    {
      "@type": "Question",
      name: "Should I include home value in net worth tracking?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "You can include home value, but many people track liquid net worth separately for practical planning.",
      },
    },
    {
      "@type": "Question",
      name: "Do I need bank account integrations to track net worth?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No. Manual snapshots can work well for consistent, private, long-term tracking.",
      },
    },
  ],
};

function webPageJsonLd(path, name, description) {
  const cleanPath = path === "/" ? "/" : path.replace(/\/+$/, "");
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name,
    description,
    url: cleanPath === "/" ? `${SEO_SITE_URL}/` : `${SEO_SITE_URL}${cleanPath}`,
  };
}

const SEO = {
  home: {
    title: "Free Cash Tracker - Track your cash over time",
    description: HOME_DESCRIPTION,
    canonicalPath: "/",
    jsonLd: [JSON_LD_ORGANIZATION, JSON_LD_WEB_APPLICATION],
  },
  dashboard: {
    title: "Dashboard | Free Cash Tracker",
    description: "Private dashboard for tracking your cash snapshots and trend over time.",
    canonicalPath: "/app",
    noindex: true,
    nofollow: true,
  },
  cashFlow: {
    title: "Monthly Cash Flow | Free Cash Tracker",
    description: "Plan monthly cash flow with one income value and an itemized monthly expenditure list.",
    canonicalPath: "/app/cash-flow",
    noindex: true,
    nofollow: true,
  },
  snapshotHistory: {
    title: "Snapshot History | Free Cash Tracker",
    description: "View your complete snapshot history.",
    canonicalPath: "/app/history",
    noindex: true,
    nofollow: true,
  },
  settings: {
    title: "Settings | Free Cash Tracker",
    description: "Manage account settings, reminders, and profile details.",
    canonicalPath: "/settings",
    noindex: true,
    nofollow: true,
  },
  terms: {
    title: "Terms of Service | Free Cash Tracker",
    description: "Read the Free Cash Tracker Terms of Service.",
    canonicalPath: "/terms",
    jsonLd: webPageJsonLd("/terms", "Terms of Service", "Read the Free Cash Tracker Terms of Service."),
  },
  privacy: {
    title: "Privacy Policy | Free Cash Tracker",
    description: "Read the Free Cash Tracker Privacy Policy.",
    canonicalPath: "/privacy",
    jsonLd: webPageJsonLd("/privacy", "Privacy Policy", "Read the Free Cash Tracker Privacy Policy."),
  },
  contact: {
    title: "Contact | Free Cash Tracker",
    description: "Get in touch with Free Cash Tracker for support and data requests.",
    canonicalPath: "/contact",
    jsonLd: webPageJsonLd("/contact", "Contact", "Get in touch with Free Cash Tracker for support and data requests."),
  },
  cookies: {
    title: "Cookies | Free Cash Tracker",
    description: "Learn how Free Cash Tracker uses essential cookies.",
    canonicalPath: "/cookies",
    jsonLd: webPageJsonLd("/cookies", "Cookies", "Learn how Free Cash Tracker uses essential cookies."),
  },
  netWorthTracker: {
    title: "Free Net Worth Tracker — Manual, Private, No Bank Linking",
    description:
      "A free net worth tracker with no bank linking. Log assets and debts (as negative balances) with quick manual snapshots and follow your net worth trend over time.",
    canonicalPath: "/net-worth-tracker",
    jsonLd: webPageJsonLd(
      "/net-worth-tracker",
      "Net Worth Tracker",
      "A free net worth tracker with no bank linking. Log assets and debts (as negative balances) with quick manual snapshots and follow your net worth trend over time."
    ),
  },
  cashTrackerApp: {
    title: "Free Cash Tracker App — No Bank Connection Required",
    description:
      "A free cash tracker app with no bank connections. Log cash, savings, and investments with quick manual snapshots and watch your trend over time. Private by default.",
    canonicalPath: "/cash-tracker-app",
    jsonLd: webPageJsonLd(
      "/cash-tracker-app",
      "Cash Tracker App",
      "A free cash tracker app with no bank connections. Log cash, savings, and investments with quick manual snapshots and watch your trend over time. Private by default."
    ),
  },
  mintAlternative: {
    title: "Free Mint Alternative With No Bank Connection (2026)",
    description:
      "Looking for a free Mint alternative that does not link your bank? Free Cash Tracker is a manual, privacy-first net worth tracker. See how it compares and how to switch.",
    canonicalPath: "/free-mint-alternative",
    jsonLd: webPageJsonLd(
      "/free-mint-alternative",
      "Free Mint Alternative With No Bank Connection",
      "Looking for a free Mint alternative that does not link your bank? Free Cash Tracker is a manual, privacy-first net worth tracker. See how it compares and how to switch."
    ),
  },
  howToTrackNetWorth: {
    title: "How to Track Net Worth (Without Linking Accounts) | Free Cash Tracker",
    description: HOW_TO_TRACK_NET_WORTH_DESCRIPTION,
    canonicalPath: "/how-to-track-net-worth",
    jsonLd: [
      webPageJsonLd(
        "/how-to-track-net-worth",
        "How to Track Net Worth (Without Linking Accounts)",
        HOW_TO_TRACK_NET_WORTH_DESCRIPTION
      ),
      JSON_LD_HOW_TO_TRACK_NET_WORTH_ARTICLE,
      JSON_LD_HOW_TO_TRACK_NET_WORTH_FAQ,
    ],
  },
  confirmEmailChange: {
    title: "Confirm Email Change | Free Cash Tracker",
    description: "Confirm your email change for your Free Cash Tracker account.",
    canonicalPath: "/confirm-email-change",
    noindex: true,
    nofollow: true,
  },
  forgotPassword: {
    title: "Forgot Password | Free Cash Tracker",
    description: "Request a password reset link for your Free Cash Tracker account.",
    canonicalPath: "/forgot-password",
    noindex: true,
    nofollow: true,
  },
  resetPassword: {
    title: "Reset Password | Free Cash Tracker",
    description: "Set a new password for your Free Cash Tracker account.",
    canonicalPath: "/reset-password",
    noindex: true,
    nofollow: true,
  },
  verifyEmail: {
    title: "Verify Email | Free Cash Tracker",
    description: "Verify your email address for your Free Cash Tracker account.",
    canonicalPath: "/verify-email",
    noindex: true,
    nofollow: true,
  },
  signup: {
    title: "Sign Up | Free Cash Tracker",
    description: "Create your Free Cash Tracker account and start tracking your cash over time.",
    canonicalPath: "/signup",
    noindex: true,
    nofollow: true,
  },
  notFound: {
    title: "Page Not Found | Free Cash Tracker",
    description: "The page you requested could not be found.",
    noindex: true,
    nofollow: true,
  },
};

function SeoPage({ seo, children }) {
  usePageSeo(seo);
  return children;
}

function computeNeedsCurrency(profile) {
  const currencyOk = String(profile?.currency || "").trim().length === 3;
  return !currencyOk;
}

const THEME_STORAGE_KEY = "fct:theme:v1";

function getInitialTheme() {
  if (typeof window === "undefined") return "light";
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // Ignore storage read errors.
  }

  if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
}

function extractErrorMessage({ json, text }, fallback) {
  if (Array.isArray(json?.details) && json.details.length) return json.details.join(" ");
  return json?.error || json?.detail || text || fallback;
}

function checkEmailPopupKey(user) {
  const ident = user?.id ?? user?.email ?? "anon";
  return `fct:check-email-popup:v1:${ident}`;
}

function hasSeenCheckEmailPopup(user) {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(checkEmailPopupKey(user)) === "1";
  } catch {
    return false;
  }
}

function markCheckEmailPopupSeen(user) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(checkEmailPopupKey(user), "1");
  } catch {
    // Ignore storage write errors.
  }
}

export default function App() {
  const [theme, setTheme] = useState(getInitialTheme);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [needsCurrency, setNeedsCurrency] = useState(false);
  const [status, setStatus] = useState("");
  const [showCheckEmailPopup, setShowCheckEmailPopup] = useState(false);
  const [checkEmailBusy, setCheckEmailBusy] = useState(false);
  const [checkEmailStatus, setCheckEmailStatus] = useState("");

  const location = useLocation();
  const navigate = useNavigate();

  const toggleTheme = useCallback(() => {
    setTheme((current) => (current === "dark" ? "light" : "dark"));
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-theme", theme);

    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute("content", theme === "dark" ? "#091224" : "#f7f4ee");
    }

    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
      } catch {
        // Ignore storage write errors.
      }
    }
  }, [theme]);

  // Allow deep-link redirects like: /?next=/app?openSnapshotModal=1
  const getSafeNext = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const next = (params.get("next") || "").trim();

    // Only allow internal paths to avoid open redirects
    if (!next.startsWith("/")) return "";
    if (next.startsWith("//")) return "";
    return next;
  }, [location.search]);

  const refreshProfileFlags = useCallback(async () => {
    const prof = await apiFetch("/api/profile/");
    if (prof.res.ok) {
      setNeedsCurrency(computeNeedsCurrency(prof.json));
    } else {
      setNeedsCurrency(false);
    }
  }, []);

  const refreshSession = useCallback(async () => {
    const who = await apiFetch("/api/auth/whoami/");
    if (!who.res.ok) {
      setUser(null);
      setNeedsCurrency(false);
      return null;
    }

    const u = who.json.user;
    setUser(u);
    await refreshProfileFlags();
    return u;
  }, [refreshProfileFlags]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        await ensureCsrf();

        const who = await apiFetch("/api/auth/whoami/");
        if (!alive) return;

        if (!who.res.ok) {
          setUser(null);
          setNeedsCurrency(false);
          return;
        }

        setUser(who.json.user);

        const prof = await apiFetch("/api/profile/");
        if (!alive) return;

        setNeedsCurrency(prof.res.ok ? computeNeedsCurrency(prof.json) : false);
      } catch (e) {
        console.error(e);
        if (!alive) return;

        setStatus(e?.message || "Startup failed.");
        setUser(null);
        setNeedsCurrency(false);
      } finally {
        if (alive) setAuthLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const doSignup = useCallback(
    async (email, password) => {
      trackEvent("signup_submit", { path: location.pathname });

      const signup = await apiFetch("/api/auth/signup/", {
        method: "POST",
        body: { email, password },
      });

      if (!signup.res.ok) {
        trackEvent("signup_failure", { path: location.pathname, status: signup.res.status });
        throw new Error(extractErrorMessage(signup, "Signup failed."));
      }

      const u = await refreshSession();
      if (!u) throw new Error("Signup succeeded but session not active yet.");

      trackEvent("signup_success", { path: location.pathname });
      setStatus("");

      // If user came from an email deep-link, send them there after auth
      const next = getSafeNext();
      if (next) navigate(next, { replace: true });

      return u;
    },
    [refreshSession, getSafeNext, location.pathname, navigate]
  );

  const doLogin = useCallback(
    async (email, password) => {
      const login = await apiFetch("/api/auth/login/", {
        method: "POST",
        body: { email, password },
      });

      if (!login.res.ok) {
        throw new Error(extractErrorMessage(login, "Login failed."));
      }

      const u = await refreshSession();
      if (!u) throw new Error("Login succeeded but session is not active yet.");

      setStatus("");

      // If user came from an email deep-link, send them there after auth
      const next = getSafeNext();
      if (next) navigate(next, { replace: true });

      return u;
    },
    [refreshSession, getSafeNext, navigate]
  );

  const doLogout = useCallback(async () => {
    try {
      await apiFetch("/api/auth/logout/", { method: "POST" });
    } finally {
      setUser(null);
      setNeedsCurrency(false);
      setShowCheckEmailPopup(false);
      setCheckEmailStatus("");
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setShowCheckEmailPopup(false);
      setCheckEmailStatus("");
      return;
    }

    // Keep this first-login nudge in the main app experience.
    if (!location.pathname.startsWith("/app")) return;

    if (user.email_verified) {
      setShowCheckEmailPopup(false);
      return;
    }

    if (hasSeenCheckEmailPopup(user)) return;

    markCheckEmailPopupSeen(user);
    setCheckEmailStatus("");
    setShowCheckEmailPopup(true);
  }, [location.pathname, user]);

  const resendVerificationFromPopup = useCallback(async () => {
    setCheckEmailBusy(true);
    setCheckEmailStatus("");
    try {
      const { res, json } = await apiFetch("/api/auth/verify-email/send/", {
        method: "POST",
      });
      if (!res.ok) {
        setCheckEmailStatus(json?.error || "Could not send verification email.");
        return;
      }
      setCheckEmailStatus("Verification email sent. Check your inbox.");
      await refreshSession();
    } catch (err) {
      setCheckEmailStatus("Network error.");
    } finally {
      setCheckEmailBusy(false);
    }
  }, [refreshSession]);

  if (authLoading) return <PageLoader label="Loading dashboard" />;

  return (
    <>
      <Suspense fallback={<PageLoader label="Loading page" />}>
        <Routes>
          <Route
            path="/"
            element={
              user ? (
                <Navigate to="/app" replace />
              ) : (
                <SeoPage seo={SEO.home}>
                  <LandingPage
                    onLogin={doLogin}
                    onSignup={doSignup}
                    status={status}
                    setStatus={setStatus}
                    next={getSafeNext()} // optional (LandingPage can ignore it)
                  />
                </SeoPage>
              )
            }
          />

          <Route
            path="/app"
            element={
              user ? (
                <SeoPage seo={SEO.dashboard}>
                  <DashboardPage user={user} onLogout={doLogout} theme={theme} onToggleTheme={toggleTheme} />
                </SeoPage>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/signup"
            element={
              user ? (
                <Navigate to="/app" replace />
              ) : (
                <SeoPage seo={SEO.signup}>
                  <SignupPage onSignup={doSignup} />
                </SeoPage>
              )
            }
          />

          <Route
            path="/app/cash-flow"
            element={
              user ? (
                <SeoPage seo={SEO.cashFlow}>
                  <CashFlowPage user={user} onLogout={doLogout} />
                </SeoPage>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/app/history"
            element={
              user ? (
                <SeoPage seo={SEO.snapshotHistory}>
                  <SnapshotHistoryPage user={user} onLogout={doLogout} />
                </SeoPage>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/settings"
            element={
              user ? (
                <SeoPage seo={SEO.settings}>
                  <SettingsPage user={user} refreshSession={refreshSession} onLogout={doLogout} />
                </SeoPage>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route path="/app/settings" element={<Navigate to="/settings" replace />} />

          <Route
            path="/terms"
            element={
              <SeoPage seo={SEO.terms}>
                <Terms />
              </SeoPage>
            }
          />
          <Route
            path="/privacy"
            element={
              <SeoPage seo={SEO.privacy}>
                <PrivacyPolicy />
              </SeoPage>
            }
          />
          <Route
            path="/contact"
            element={
              <SeoPage seo={SEO.contact}>
                <ContactPage />
              </SeoPage>
            }
          />
          <Route
            path="/cookies"
            element={
              <SeoPage seo={SEO.cookies}>
                <CookiesPage />
              </SeoPage>
            }
          />
          <Route
            path="/net-worth-tracker"
            element={
              <SeoPage seo={SEO.netWorthTracker}>
                <NetWorthTrackerPage />
              </SeoPage>
            }
          />
          <Route
            path="/cash-tracker-app"
            element={
              <SeoPage seo={SEO.cashTrackerApp}>
                <CashTrackerAppPage />
              </SeoPage>
            }
          />
          <Route
            path="/free-mint-alternative"
            element={
              <SeoPage seo={SEO.mintAlternative}>
                <MintAlternativePage />
              </SeoPage>
            }
          />
          <Route
            path="/how-to-track-net-worth"
            element={
              <SeoPage seo={SEO.howToTrackNetWorth}>
                <HowToTrackNetWorthPage />
              </SeoPage>
            }
          />
          <Route
            path="/confirm-email-change/:uidb64/:token"
            element={
              <SeoPage seo={SEO.confirmEmailChange}>
                <ConfirmEmailChangePage refreshSession={refreshSession} />
              </SeoPage>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <SeoPage seo={SEO.forgotPassword}>
                <ForgotPasswordPage />
              </SeoPage>
            }
          />
          <Route
            path="/reset-password"
            element={
              <SeoPage seo={SEO.resetPassword}>
                <ResetPasswordPage />
              </SeoPage>
            }
          />
          <Route
            path="/verify-email/:uidb64/:token"
            element={
              <SeoPage seo={SEO.verifyEmail}>
                <VerifyEmailPage refreshSession={refreshSession} />
              </SeoPage>
            }
          />
          <Route path="/login" element={<Navigate to="/" replace />} />

          <Route
            path="*"
            element={
              <SeoPage seo={SEO.notFound}>
                <div style={{ padding: "2rem" }}>Not found</div>
              </SeoPage>
            }
          />
        </Routes>
      </Suspense>

      <Suspense fallback={null}>
        {user && needsCurrency && location.pathname.startsWith("/app") ? (
          <CurrencyRequiredModal
            onSaved={async () => {
              await refreshSession();
            }}
          />
        ) : null}
      </Suspense>

      {user && showCheckEmailPopup ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "var(--overlay, rgba(15, 23, 42, 0.45))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              width: "min(520px, 100%)",
              borderRadius: 16,
              border: "1px solid var(--border, #e2e8f0)",
              background: "var(--panel, #ffffff)",
              padding: "1.25rem",
              boxShadow: "var(--shadow, 0 24px 60px rgba(15, 23, 42, 0.2))",
            }}
          >
            <div style={{ fontSize: "1.2rem", fontWeight: 800, marginBottom: 8, color: "var(--ink, #0f172a)" }}>
              Check your email
            </div>
            <div style={{ color: "var(--muted, #475569)", lineHeight: 1.5 }}>
              We sent a verification link to your email. Verify your address to enable reminders and account
              recovery.
            </div>
            {checkEmailStatus ? (
              <div style={{ marginTop: 12, color: "var(--ink, #0f172a)", fontWeight: 600 }}>{checkEmailStatus}</div>
            ) : null}
            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                onClick={() => setShowCheckEmailPopup(false)}
                disabled={checkEmailBusy}
                style={{
                  padding: "0.5rem 0.85rem",
                  borderRadius: 10,
                  border: "1px solid var(--border, #e2e8f0)",
                  background: "var(--panel, #ffffff)",
                  color: "var(--ink, #0f172a)",
                  cursor: checkEmailBusy ? "not-allowed" : "pointer",
                }}
              >
                Close
              </button>
              <button
                type="button"
                onClick={resendVerificationFromPopup}
                disabled={checkEmailBusy}
                style={{
                  padding: "0.5rem 0.85rem",
                  borderRadius: 10,
                  border: "1px solid var(--accent-strong, #0284c7)",
                  background: "linear-gradient(135deg, var(--accent, #0ea5e9), var(--accent-strong, #0284c7))",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: checkEmailBusy ? "not-allowed" : "pointer",
                }}
              >
                {checkEmailBusy ? "Sending..." : "Resend email"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <Suspense fallback={null}>
        <Analytics />
      </Suspense>
    </>
  );
}
